import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Bell, Loader2, Trash2, CheckCheck } from "lucide-react";
import { toast } from "sonner";

const LEVEL = { info: "bg-blue-500", success: "bg-emerald-500", attention: "bg-amber-500", urgent: "bg-red-500" };
const CAT = { appointments: "RDV", quotes: "Devis", invoices: "Factures", emails: "Emails", clients: "Clients", tasks: "Tâches", contracts: "Contrats", waitlist: "Liste d'attente", satisfaction: "Satisfaction", loyalty: "Fidélité", team: "Équipe", accounting: "Comptabilité", integrations: "Intégrations", subscription: "Abonnement" };

export default function Notifications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/notifications")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const open = async (n) => { if (!n.read) { await api.post(`/notifications/${n.id}/read`); } if (n.link) navigate(n.link); else load(); };
  const del = async (id) => { await api.delete(`/notifications/${id}`); load(); };
  const markAll = async () => { await api.post("/notifications/read-all"); toast.success("Tout marqué comme lu"); load(); };
  const delRead = async () => { const { data } = await api.post("/notifications/delete-read"); toast.success(`${data.deleted} supprimée(s)`); load(); };

  const shown = rows.filter((n) => filter === "all" ? true : filter === "unread" ? !n.read : n.category === filter);
  const cats = [...new Set(rows.map((r) => r.category))];

  return (
    <div>
      <PageHeader title="Notifications" subtitle="Votre centre de notifications" icon={Bell}
        actions={<div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={markAll} data-testid="notif-markall-btn" className="border-slate-200"><CheckCheck className="w-4 h-4 mr-1" /> Tout lu</Button>
          <Button size="sm" variant="outline" onClick={delRead} data-testid="notif-delread-btn" className="border-slate-200"><Trash2 className="w-4 h-4 mr-1" /> Vider les lues</Button>
        </div>} />

      <div className="flex gap-2 mb-4 flex-wrap">
        {[["all", "Toutes"], ["unread", "Non lues"]].concat(cats.map((c) => [c, CAT[c] || c])).map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`notif-filter-${k}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        : shown.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">Aucune notification.</div>
        : <div className="space-y-2">{shown.map((n) => (
          <div key={n.id} data-testid={`notif-row-${n.id}`} className={`bg-white border rounded-xl p-4 flex items-start gap-3 ${n.read ? "border-slate-200 opacity-70" : "border-emerald-200"}`}>
            <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${LEVEL[n.level] || "bg-slate-400"}`} />
            <button className="flex-1 min-w-0 text-left" onClick={() => open(n)}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-800">{n.title}</span>
                <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{CAT[n.category] || n.category}</span>
              </div>
              {n.message && <div className="text-xs text-slate-500 mt-0.5">{n.message}</div>}
              <div className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString("fr-FR")}</div>
            </button>
            <button onClick={() => del(n.id)} data-testid={`notif-del-${n.id}`} className="p-2 rounded-lg hover:bg-red-500/10 text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}</div>}
    </div>
  );
}
